import Vue from 'vue';
import firebase from 'firebase/app';
import { writePageScale } from '@/lib/page-scale-storage';
import { writeViewSetting } from '@/lib/view-mode-storage';
import 'firebase/storage';
import 'firebase/functions';
import { db } from '@/main';
import repos from '@/data';
import NavBus from '@/buses/nav';
import {PROJECT_CREATE_VERSION_URL, PROJECT_PROMOTE_VERSION_URL} from '@/lib/URLs';
import { labelsToRestore } from '@/lib/page-names';
import Batch from '@/lib/batch';
import { suppressShortcuts } from '@/lib/shortcutSuppress';
import { getRecentErrors } from '@/lib/errorBuffer';
import router from '@/router';
import Utils from '@/lib/Utils';
import { resolveContacts } from '@/lib/contacts';
import PDFJS from 'pdfjs-dist/webpack';
import { peerProjectSettings, projectAccessInfo } from '@/lib/user-profiles';
import { observerOptionsMatter, checkAccessChange } from '@/lib/observer-access';
import { checkCuePageWrite } from '@/lib/cue-page';
import { withoutDrawingFields } from '@/lib/cue-drawing-fields';
import { selectPrune, promoteDetail } from '@/lib/history-autosave';
import { duplicateCopyFields, duplicatorProjectSettings } from '@/lib/duplicate-project-fields';
import { writeLabelsIfUnchanged } from '@/lib/page-labels';
import { saveVersionOnServer } from '@/lib/version-save';
import { usesServerVersionSave } from '@/lib/server-switches';
import { withCreator } from '@/lib/layer-creator';
import { localUserPatch } from '@/lib/user-name';
import { renumberPagesOnServer, bookmarkChanges } from '@/lib/server-page-change';
import { takePageChangeLock, releasePageChangeLock } from '@/lib/page-change-lock';
import { waitForShowEditsSent } from '@/lib/show-edits-sent';
import { addsNewLayerToSplit } from '@/lib/split-layers';
import { cueWriteNotifiesNotesList } from '@/lib/noteslist-cue-fields';
import integrationActions from './integration-actions';
import markerActions from './marker-actions';

PDFJS.workerSrc = '../../../node_modules/pdfjs-dist/build/pdf.worker.js';

export default {
    updatePageOffset({ state, getters }, offset) {
        state.pageOffset = offset;
    },
    patchActiveProjectFields({ state }, patch) {
        if (state.activeProject) {
            // Replace the object so Vuex getters (e.g. `project`) invalidate.
            // In-place Object.assign does not update the UI when Notes List
            // fields are added for the first time (Vue 2 reactivity + getter cache).
            state.activeProject = {
                ...state.activeProject,
                ...patch
            };
        }
    },
    lockProject({ getters }, value) {
        try {
            const projectRef = db.doc(getters.projectPath);
            projectRef.update({
                locked: value
            });
        } catch (e) {
            console.error(e);
        }
    },
    async deleteProject({ getters }, projectID) {
        try {
            const project = await getters.projectFromID(projectID);
            if (!project) return;
            const batch = new Batch();
            const projectRef = db.collection('projects').doc(project.id);
    
            // Fetch subcollections: cues, notes, layers, scriptMarkers, versions
            // with cache handling.
            //
            // scriptMarkers added 23 Aug 2026. Firestore does NOT cascade-delete
            // subcollections, so without this a deleted project left its whole
            // structure-marker collection stranded. Now that versions carry markers
            // too (#386), the version loop below needs the same treatment or every
            // deleted project orphans one collection per version.
            const [cuesSnapshot, notesSnapshot, layersSnapshot, markersSnapshot, versionsSnapshot] = await Promise.all([
                projectRef.collection('cues').get({ source: 'cache' }),  // Use cache first
                projectRef.collection('notes').get({ source: 'cache' }),
                projectRef.collection('layers').get({ source: 'cache' }),
                projectRef.collection('scriptMarkers').get({ source: 'cache' }),
                projectRef.collection('versions').get({ source: 'cache' })
            ]);
    
            // Add cue, notes, layers documents to batch delete
            for (const cueDoc of cuesSnapshot.docs) {
                batch.delete(cueDoc.ref);
            }
            for (const noteDoc of notesSnapshot.docs) {
                batch.delete(noteDoc.ref);
            }
            for (const layerDoc of layersSnapshot.docs) {
                batch.delete(layerDoc.ref);
            }
            for (const markerDoc of markersSnapshot.docs) {
                batch.delete(markerDoc.ref);
            }
    
            // Handle versions and their subcollections
            const versionSubPromises = [];
    
            for (const versionDoc of versionsSnapshot.docs) {
                versionSubPromises.push(
                    versionDoc.ref.collection('cues').get({ source: 'cache' }),
                    versionDoc.ref.collection('notes').get({ source: 'cache' }),
                    versionDoc.ref.collection('layers').get({ source: 'cache' }),
                    versionDoc.ref.collection('scriptMarkers').get({ source: 'cache' })
                );
                batch.delete(versionDoc.ref);
            }
    
            // Resolve all subcollection documents for versions and delete them
            const subSnapshots = await Promise.all(versionSubPromises);
            for (const snapshot of subSnapshots) {
                for (const doc of snapshot.docs) {
                    batch.delete(doc.ref);
                }
            }
    
            // Handle collaborators and invitations
            for (const collaborator of getters.pendingCollaborators) {
                batch.delete(db.collection('invitations').doc(collaborator.id));
            }
    
            // Remove project from the owner's project list
            batch.update(db.collection('users').doc(project.ownerUID), {
                [`projects.${project.id}`]: firebase.firestore.FieldValue.delete()
            });
    
            batch.delete(projectRef);
    
            // Commit the batch
            await batch.commit();
        } catch (e) {
            console.error('Error deleting project:', e);
        }
    },
    updateProjectOwnerRole({ getters }, ownerRole) {
        db.collection('projects').doc(getters.project.id).update({
            ownerRole
        });
    },
    // COLLABORATOR FUNCTIONS
    addPendingCollaborator({ getters }, { project, collaborator, inviter }) {
        const newPendingCollab = db.collection('invitations').doc();

        collaborator.id = newPendingCollab.id;

        collaborator.pid = project.id;

        // Store the email canonically (lowercased). The dashboard binds pending
        // invites with .where('email','==', email.toLowerCase()), so a mixed-case
        // invite would otherwise never appear for the invitee — the long-standing
        // "invite silently never lands" bug.
        if (collaborator.email) {
            collaborator.email = collaborator.email.toString().trim().toLowerCase();
        }

        // Timestamp so outbound history can be ordered most-recent-first.
        if (!collaborator.createdAt) {
            collaborator.createdAt = new Date().toISOString();
        }

        if (inviter) {
            collaborator.inviter = inviter;
        }

        newPendingCollab.set(collaborator);
    },
    // Build the "People you've worked with" suggestion list for the invite UI.
    // Sources (all resolved on demand, so this never depends on the projects
    // collection being bound — the editor doesn't bind it):
    //   1. Previous collaborators — read my project docs directly and collect the
    //      collaborator maps (each entry already carries a name; email is resolved
    //      lazily when the user picks one, in the component).
    //   2. People who invited me — the OWNERS of projects I'm a collaborator on
    //      (durable; accepted invites are deleted from `invitations`), plus anyone
    //      with a still-pending invite addressed to me.
    //   3. Affiliated members — for an admin, active (non-false) affiliateUsers.
    // `cache` is an injected Map (uid -> {uid,email,username}) owned by the caller.
    // Every related uid is resolved to the account's full name (`username`, i.e.
    // "First Last" from signup) + email, so rows show real first+last names and
    // can be filtered by email. The collaborator map's typed `name` is only a
    // fallback when a user doc has no username.
    async fetchInviteSuggestions({ getters }, { cache } = {}) {
        const user = getters.user;
        if (!user || !user.uid) return [];
        const selfUid = user.uid;
        const myEmail = (user.email || '').toLowerCase();

        const projectIds = Object.keys(user.projects || {});

        const projectSnaps = await Promise.all(
            projectIds.map(pid =>
                db.collection('projects').doc(pid).get().catch(() => null)
            )
        );

        const collaboratorUids = new Set();  // co-collaborators across my projects
        const inviterUids = new Set();        // owners of my projects + pending inviters
        const fallbackNames = {};             // uid -> typed name, used only if no username
        projectSnaps.forEach((snap) => {
            if (!snap || !snap.exists) return;
            const p = snap.data() || {};
            const map = p.collaborators || {};
            Object.keys(map).forEach((uid) => {
                if (uid === selfUid) return;
                collaboratorUids.add(uid);
                if (!fallbackNames[uid] && map[uid] && map[uid].name) {
                    fallbackNames[uid] = map[uid].name;
                }
            });
            if (p.ownerUID && p.ownerUID !== selfUid) {
                inviterUids.add(p.ownerUID);
            }
        });

        // Still-pending invites addressed to me -> their inviters.
        if (myEmail) {
            const inboundSnap = await db.collection('invitations')
                .where('email', '==', myEmail).get();
            inboundSnap.docs.forEach((d) => {
                const inv = d.data();
                if (inv && inv.inviter && inv.inviter !== selfUid) {
                    inviterUids.add(inv.inviter);
                }
            });
        }

        const affiliateUsers = user.affiliateUsers || {};
        const affiliateUids = new Set(
            Object.keys(affiliateUsers).filter(uid => affiliateUsers[uid] && uid !== selfUid)
        );

        // Resolve every related uid to {uid,email,username}; unique by uid, cached.
        const allUids = new Set([...collaboratorUids, ...inviterUids, ...affiliateUids]);
        const resolver = uid => getters.userByUID(uid);
        const resolved = await resolveContacts(resolver, allUids, cache);

        // Tag each row by source (collaborator > inviter > affiliate) and use the
        // account's full name, falling back to the typed collaborator name.
        const sourceFor = (uid) => {
            if (collaboratorUids.has(uid)) return 'collaborator';
            if (inviterUids.has(uid)) return 'inviter';
            return 'affiliate';
        };
        return resolved.map(u => ({
            uid: u.uid,
            email: u.email,
            name: u.username || fallbackNames[u.uid] || '',
            source: sourceFor(u.uid)
        }));
    },
    removePendingCollaborator({ getters }, { pendingCollaboratorID }) {
        try {
            db.collection('invitations').doc(pendingCollaboratorID).delete();
        } catch (e) {
            console.error(e);
        }
    },
    // AFFILIATE INVITE FUNCTIONS (mirror the collaborator invite pattern)
    // Email-keyed like project invites, so an invitee without an account yet is
    // reconciled on login by the email-scoped dashboard binding.
    inviteToAffiliate({ getters }, { email, name }) {
        const doc = db.collection('affiliateInvitations').doc();
        return doc.set({
            id: doc.id,
            email: (email || '').toLowerCase(),
            name: name || '',
            adminUid: getters.user.uid,
            institutionName: getters.user.institution_name || '',
            createdAt: new Date().toISOString()
        });
    },
    acceptAffiliateInvitation({ getters }, { invitation }) {
        return new Promise((resolve, reject) => {
            (async () => {
                try {
                    const batch = new Batch();
                    // Affiliate the current user with the inviting institution.
                    // affiliateSeatRequested marks this as an ADMIN-INVITED accept
                    // (vs unsolicited self-affiliation) so the CF consumes an open
                    // seat only for invited users; the CF clears the marker.
                    batch.update(db.collection('users').doc(getters.user.uid), {
                        affiliateInstitution: invitation.adminUid,
                        affiliateSeatRequested: true
                    });
                    // Delete this invite plus any other pending affiliate invites
                    // for this email — affiliateInstitution is single-valued, so
                    // the rest could never be accepted and would dangle.
                    const email = (invitation.email || getters.user.email || '').toLowerCase();
                    const others = await db.collection('affiliateInvitations')
                        .where('email', '==', email).get();
                    others.docs.forEach(d => batch.delete(d.ref));
                    if (others.empty && invitation.id) {
                        batch.delete(db.collection('affiliateInvitations').doc(invitation.id));
                    }
                    // Await the commit before resolving. The caller reloads the page
                    // on success (dashboard handleAffiliationAccepted), and an
                    // un-awaited commit raced that reload: the write was cancelled,
                    // the invite came straight back, and the admin saw it still
                    // pending (Josh, sandbox, 23 Sept 2026; present since 0cbf5a2a,
                    // 10 July). addCollaborator had the same trap and fixed it.
                    await batch.commit();
                    resolve();
                } catch (e) {
                    console.error(e);
                    reject(e instanceof Error ? e : new Error(String(e)));
                }
            })();
        });
    },
    // The invitee cannot join (Josh, 23 Sept 2026: they already pay for their own
    // subscription). Rules let the invitee set exactly these three fields on their
    // own invite; the admin's Manage Affiliation list shows the reason.
    markAffiliateInvitationFailed({ getters }, { invitationID, reason }) {
        return db.collection('affiliateInvitations').doc(invitationID).update({
            status: 'failed',
            failedReason: reason,
            failedAt: new Date().toISOString()
        }).catch((e) => {
            console.error('Could not mark affiliate invitation failed:', e);
        });
    },
    declineAffiliateInvitation({ getters }, { invitationID }) {
        try {
            db.collection('affiliateInvitations').doc(invitationID).delete();
        } catch (e) {
            console.error(e);
        }
    },
    addCollaborator({ getters, dispatch }, { project, uid, pendingCollaborator }) {
        return new Promise((resolve, reject) => {
            (async () => {
                try {
                    const projectId = project && (project.id || project.pid);
                    if (!projectId) {
                        throw new Error('addCollaborator: project.id is required');
                    }
                    if (!pendingCollaborator || !pendingCollaborator.id) {
                        throw new Error('addCollaborator: pendingCollaborator.id is required');
                    }
                    const previousCollaborators = { ...(project.collaborators || {}) };
                    const previousOwnerUID = project.ownerUID || null;
                    const collab = {
                        uid: uid,
                        layers: {},
                        role: pendingCollaborator.role,
                        name: pendingCollaborator.name,
                        type: 'member',
                        canEditStructure: false,
                        // Projects write rules (Sun 18 Oct 2026): the invitee writes
                        // their own entry, so the rule needs proof of an invitation
                        // for THIS project addressed to their sign-in email. It
                        // reads this id (the batch below deletes the invitation;
                        // rules see the pre-batch state). Without it the accept is
                        // refused. Ship the frontend BEFORE the rules.
                        invitationId: pendingCollaborator.id
                    };
                    if (!project.layers) {
                        project.layers = await db.collection('projects').doc(projectId).collection('layers')
                        .get()
                        .then((snapshot) => {
                            const list = [];
                            for (const doc of snapshot.docs) {
                                list.push({
                                    id: doc.id
                                });
                            }
                            return list;
                        });
                    }
                    if (project.layers) {
                        for (const layer of project.layers) {
                            collab.layers[layer.id] = false;
                        }
                    }
                    const layerIDs = (project.layers && project.layers.map(layer => layer.id)) || [];

                    const batch = new Batch();
                    batch.update(db.collection('projects').doc(projectId), {
                        ['collaborators.' + uid]: collab
                    });
                    batch.delete(db.collection('invitations').doc(pendingCollaborator.id));
                    batch.update(db.collection('users').doc(uid), {
                        ['projects.' + projectId + '.lastChangedDate']: new Date().toISOString(),
                        ['projects.' + projectId + '.layers.viewing']: layerIDs,
                        ['projects.' + projectId + '.layers.listViewing']: layerIDs,
                        ['projects.' + projectId + '.layers.showNotes']: layerIDs,
                        ['projects.' + projectId + '.layers.showCues']: layerIDs
                    });
                    // Await the commit before resolving: the caller (accept-invite)
                    // reloads the page immediately on success, and a fire-and-forget
                    // commit races that reload — the writes get interrupted and the
                    // project never lands. Awaiting guarantees they persist first.
                    await batch.commit();

                    // Accept runs as the invitee — HTTP roster/sync is required
                    // because Firestore triggers are ignored under --only functions.
                    console.log(
                        '[noteslist-integration] accept → roster/sync project=' +
                            projectId +
                            ' added=' +
                            uid
                    );
                    try {
                        const syncResult = await dispatch('syncNotesListProjectRoster', {
                            projectId,
                            previousCollaborators,
                            previousOwnerUID,
                            addedUids: [uid],
                            removedUids: []
                        });
                        console.log(
                            '[noteslist-integration] accept → roster/sync ok',
                            syncResult
                        );
                    } catch (syncErr) {
                        console.error(
                            '[noteslist-integration] roster sync after accept FAILED — is the Functions emulator running on :5001?',
                            syncErr && syncErr.message,
                            syncErr
                        );
                    }

                    resolve();
                } catch (e) {
                    console.error(e);
                    reject(e instanceof Error ? e : new Error(String(e)));
                }
            })();
        });
    },
    updateCollaborator({ getters, dispatch }, collaborator) {
        const project = getters.project;
        const previousCollaborators = { ...(project.collaborators || {}) };
        const previousOwnerUID = project.ownerUID || null;
        return db.collection('projects').doc(project.id).update({
            ['collaborators.' + collaborator.uid]: collaborator
        }).then(() =>
            dispatch('syncNotesListProjectRoster', {
                projectId: project.id,
                previousCollaborators,
                previousOwnerUID
            }).catch((syncErr) => {
                console.warn(
                    '[noteslist-integration] roster sync after updateCollaborator failed:',
                    syncErr.message
                );
            })
        );
    },
    removeCollaborator({ getters, dispatch }, { project, collaboratorID }) {
        const removedUid =
            collaboratorID ||
            null;
        if (!removedUid) {
            console.error(
                '[noteslist-integration] removeCollaborator called without collaboratorID — collaborator_removed will not fire'
            );
            return Promise.reject(new Error('collaboratorID is required'));
        }
        const liveProject =
            getters.project && getters.project.id === project.id
                ? getters.project
                : project;
        const previousCollaborators = {
            ...(liveProject.collaborators || project.collaborators || {})
        };
        // Guarantee the removed uid is visible to any diff fallback.
        if (!previousCollaborators[removedUid]) {
            previousCollaborators[removedUid] = {
                uid: removedUid,
                type: 'member'
            };
        }
        const previousOwnerUID = liveProject.ownerUID || project.ownerUID || null;

        // Project write is authoritative for roster sync. Do NOT gate sync on
        // the user-doc cleanup — if that fails, collaborator_removed never fired.
        return db
            .collection('projects')
            .doc(project.id)
            .update({
                ['collaborators.' + removedUid]: firebase.firestore.FieldValue.delete()
            })
            .then(async () => {
                try {
                    await repos.users.updateOther(removedUid, {
                        ['projects.' + project.id]: firebase.firestore.FieldValue.delete()
                    });
                } catch (userErr) {
                    console.warn(
                        '[noteslist-integration] user project cleanup after remove failed:',
                        userErr.message
                    );
                }
                try {
                    await dispatch('syncNotesListProjectRoster', {
                        projectId: project.id,
                        previousCollaborators,
                        previousOwnerUID,
                        addedUids: [],
                        // Wire contract event is collaborator_removed (not collaborator_revoke).
                        removedUids: [removedUid]
                    });
                } catch (syncErr) {
                    console.warn(
                        '[noteslist-integration] roster sync after removeCollaborator failed:',
                        syncErr.message
                    );
                }
            });
    },
    async updateCollaboratorOptions({ getters, dispatch }, {uid, role, type, canEditStructure}) {
        const project = getters.project;
        // Observer accounts (1 Oct 2026): Administrator and Structure grant nothing
        // to an unpaid account on a normal project, so they are refused here as well
        // as greyed out in the dialog. Asks the server only when it could matter.
        if (observerOptionsMatter(project) && (type === 'administrator' || canEditStructure === true)) {
            const current = (project.collaborators || {})[uid] || {};
            checkAccessChange({
                project,
                uid,
                type,
                canEditStructure,
                currentType: current.type,
                currentCanEditStructure: current.canEditStructure === true,
                accessInfo: await projectAccessInfo(project.id)
            });
        }
        const previousCollaborators = { ...(project.collaborators || {}) };
        const previousOwnerUID = project.ownerUID || null;
        const updateOptions = {};
        if (role) {
            updateOptions['collaborators.' + uid + '.role'] = role;
        }
        if (type) {
            updateOptions['collaborators.' + uid + '.type'] = type;
        }
        if (typeof canEditStructure === 'boolean') {
            updateOptions['collaborators.' + uid + '.canEditStructure'] = canEditStructure;
        }
        return db.collection('projects').doc(project.id).update(updateOptions).then(() =>
            dispatch('syncNotesListProjectRoster', {
                projectId: project.id,
                previousCollaborators,
                previousOwnerUID
            }).catch((syncErr) => {
                console.warn(
                    '[noteslist-integration] roster sync after updateCollaboratorOptions failed:',
                    syncErr.message
                );
            })
        );
    },
    async transferProjectOwnership({ getters, dispatch }, {target}) {
        const project = getters.project;
        // Observer accounts (1 Oct 2026): ownership of a normal project cannot go to
        // an unpaid account, which could not edit it and would lose owner controls.
        if (observerOptionsMatter(project)) {
            checkAccessChange({
                project,
                uid: target.uid,
                type: 'transfer',
                accessInfo: await projectAccessInfo(project.id)
            });
        }
        const previousCollaborators = { ...(project.collaborators || {}) };
        const previousOwnerUID = project.ownerUID || null;
        const transferOptions = {
            ownerUID: target.uid,
            ['collaborators.' + target.uid]: firebase.firestore.FieldValue.delete(),
            ['collaborators.' + getters.project.ownerUID]: {
                uid: getters.user.uid,
                name: getters.user.username,
                role: 'Transferred',
                type: 'administrator',
                layers: getters.layers.reduce((layers, layer) => ({
                    ...layers,
                    [layer.id]: true
                }), {})
            }
        };
        return db.collection('projects').doc(project.id).update(transferOptions).then(() =>
            dispatch('syncNotesListProjectRoster', {
                projectId: project.id,
                previousCollaborators,
                previousOwnerUID
            }).catch((syncErr) => {
                console.warn(
                    '[noteslist-integration] roster sync after transferProjectOwnership failed:',
                    syncErr.message
                );
            })
        );
    },

    // NOTE FUNCTIONS
    addUserNote({ getters }, { note, layer }) {
        // New note (text/stamp/etc.) auto-focuses a field; suppress tool shortcuts
        // so a keystroke in the focus gap types instead of firing a shortcut.
        suppressShortcuts();
        return new Promise((resolve, reject) => {
            (async () => {
                try {
                    let newNoteRef = null;
                    const projectRef = db.doc(getters.projectPath);
                    if (note.id) {
                        newNoteRef = projectRef.collection('notes').doc(note.id);
                    } else {
                        newNoteRef = projectRef.collection('notes').doc();
                    }

                    const layerDefaultBold = layer.defaultBold ? layer.defaultBold : false;
                    const layerDefaultItalic = layer.defaultItalic ? layer.defaultItalic : false;
                    if (note.type === 'line') {
                        if (!note.height) {
                            note.height = layer.lineThikness;
                        }
                    }
                    if (note.type === 'stamp') {
                        if (!note.stampSize) {
                            note.stampSize = layer.stampSize;
                        }
                    }
                    const noteData = {
                        id: newNoteRef.id,
                        text: note.text || '',
                        x: note.x || 0,
                        y: note.y || 0,
                        width: note.width || 100,
                        height: note.height || 24,
                        page: note.pageNumber,
                        layer: layer.id,
                        font: note.font || layer.fontFamily || 'Arial',
                        size: note.size || layer.textFontSize || 14,
                        color: note.color || (note.type === 'highlight' ? layer.highlightColor : layer.primaryColor) || '#000000',
                        color2: note.color2 || layer.secondaryColor || '#000000',
                        type: note.type || 'text',
                        subType: (note.subType != null) ? note.subType : '',
                        bold: (note.bold != null) ? note.bold : layerDefaultBold,
                        italic: (note.italic != null) ? note.italic : layerDefaultItalic,
                        opacity: (note.opacity != null) ? note.opacity : 20,
                        stampSize: note.stampSize || 40,
                        lineThikness: layer.lineThikness || 2,
                        coords: note.coords || [],
                        metadata: note.metadata || {}
                    };
                    await newNoteRef.set(noteData);
                    resolve({
                        id: noteData.id,
                        type: noteData.type
                    });
                } catch (e) {
                    console.error(e);
                    reject(e);
                }
            })();
        });
    },
    updateUserNote({ getters }, { note }) {
        try {
            const projectRef = db.doc(getters.projectPath);
            const noteRef = projectRef.collection('notes').doc(note.id);
            noteRef.update({
                text: note.text,
                x: note.x,
                y: note.y,
                width: note.width,
                height: note.height,
                page: note.page,
                layer: note.layer,
                font: note.font || 'Arial',
                size: note.size || 14,
                color: note.color || '#000000',
                color2: note.color2 || '#000000',
                type: note.type || 'text',
                subType: (note.subType != null) ? note.subType : '',
                bold: note.bold || false,
                italic: note.italic || false,
                opacity: (note.opacity != null) ? note.opacity : 20,
                stampSize: note.stampSize || 40,
                lineThikness: note.lineThikness || 2,
                background: note.background || ''
            });
        } catch (e) {
            console.error(e);
        }
    },
    removeUserNote({ getters }, { note }) {
        try {
            const projectRef = db.doc(getters.projectPath);
            const noteRef = projectRef.collection('notes').doc(note.id);
            noteRef.delete();
        } catch (e) {
            console.error(e);
        }
    },

    // Self-service escape hatch: a member turns their OWN preview off. Sets
    // userPaused on their featureFlags doc (rules allow the user to set it true
    // but not false — an admin resumes). Resolves from cache when offline so the
    // features drop immediately even mid-bug.
    pausePreview({ getters }) {
        const user = getters.user;
        if (!user || !user.uid) {
            return Promise.resolve();
        }
        return db.collection('featureFlags').doc(user.uid).update({ userPaused: true });
    },

    // BETA BUG REPORTER
    // Writes a report to the top-level bugReports collection (isolated from all
    // existing triggers). Returns the new doc id so the UI can show a reference.
    // Resolves offline too (Firestore resolves .set from local cache) — the
    // caller distinguishes "sent" vs "queued" via navigator.onLine.
    submitBugReport({ getters }, { title, description, severity, module, browser, os, build, environment }) {
        const user = getters.user;
        const ref = db.collection('bugReports').doc();
        const payload = {
            id: ref.id,
            uid: (user && user.uid) || null,
            email: (user && user.email) || '',
            title: (title || '').slice(0, 200),
            description: (description || '').slice(0, 4999),
            severity: severity || 'Medium',
            module: module || '',
            browser: browser || '',
            os: os || '',
            // Which build the reporter was running, and where. `appVersion` below is
            // NOT this: it carries APP_ENV, which is a build flag rather than a
            // deployed-commit identity, and is 'production' on more than one host.
            // `environment` is derived from the Firebase project id instead, which
            // cannot be ambiguous about which backend they were actually talking to.
            build: build || '',
            environment: environment || '',
            url: window.location.href,
            projectId: getters.projectID || '',
            versionId: (getters.currentVersion && getters.currentVersion.id) || '',
            userAgent: navigator.userAgent,
            viewport: `${window.innerWidth}x${window.innerHeight}`,
            appVersion: process.env.APP_ENV || '',
            consoleErrors: getRecentErrors(),
            createdAt: firebase.firestore.FieldValue.serverTimestamp()
        };
        return ref.set(payload).then(() => ref.id);
    },

    // LAYER FUNCTIONS
    addLayer({ getters, dispatch }, layer) {
        try {
            const projectRef = db.doc(getters.projectPath);
            const newLayerRef = projectRef.collection('layers').doc();

            let shouldBeEditing = false;
            if (!getters.layers.length) {
                shouldBeEditing = true;
            }

            layer.id = newLayerRef.id;
            newLayerRef.set(withCreator(layer, getters.user && getters.user.uid, new Date()))
                .then(() => dispatch('queueNotesListStructureEvent', { event: 'layer.created', layerId: layer.id }))
                .catch(() => {});

            if (getters.user.uid !== getters.project.ownerUID) {
                db.collection('projects').doc(getters.project.id).update({
                    ['collaborators.' + getters.user.uid + '.layers.' + layer.id]: true
                });
            }

            if (shouldBeEditing) {
                dispatch('setEditingLayer', {layer, type: 'cue'});
                dispatch('setEditingLayer', {layer, type: 'note'});
            }
        } catch (e) {
            console.error(e);
        }
    },
    updateLayer({ getters, dispatch }, layer) {
        try {
            const projectRef = db.doc(getters.projectPath);
            const layerRef = projectRef.collection('layers').doc(layer.id);
            // Notes List sees the name and color only; it is told on every save (debounced).
            const notifyNotesList = () => dispatch('queueNotesListStructureEvent', { event: 'layer.updated', layerId: layer.id })
                .catch(() => {});
            layerRef.update({
                color: layer.color,
                boxOpacity: (layer.boxOpacity != null) ? layer.boxOpacity : 20,
                lineOpacity: (layer.lineOpacity != null) ? layer.lineOpacity : 30,
                title: layer.title,
                attributes: layer.attributes || {},
                fontFamily: layer.fontFamily || 'Arial',
                titleFontSize: layer.titleFontSize || 14,
                defaultCueMarker: layer.defaultCueMarker || 'position',
                defaultCuePlacement: layer.defaultCuePlacement || 'right',
                defaultTextStampBackground: layer.defaultTextStampBackground || 'nofill',
                defaultTextTool: layer.defaultTextTool || 'text',
                textFontSize: layer.textFontSize || 10,
                defaultBold: layer.defaultBold ? layer.defaultBold : false,
                defaultItalic: (layer.defaultItalic != null) ? layer.defaultItalic : false,
                primaryColor: layer.primaryColor || '#000000',
                secondaryColor: layer.secondaryColor || '#000000',
                highlightColor: layer.highlightColor || '#000000',
                stampSize: layer.stampSize || 40,
                lineThikness: layer.lineThikness || 2,
                matchPrimaryColor: layer.matchPrimaryColor || false,
                matchSecondaryColor: layer.matchSecondaryColor || false,
                matchHighlightColor: layer.matchHighlightColor || false
            }).then(notifyNotesList);
        } catch (e) {
            console.error(e);
        }
    },
    removeLayer({ getters, dispatch }, layer) {
        try {
            const projectRef = db.doc(getters.projectPath);
            const cues = getters.cues;

            const layerCues = cues.filter((cue) => {
                return cue.layer === layer.id;
            });

            const deletes = [];
            for (let i = layerCues.length - 1; i >= 0; i--) {
                const cue = layerCues[i];
                const cueRef = projectRef.collection('cues').doc(cue.id);
                deletes.push(cueRef.delete());
            }

            const layerRef = projectRef.collection('layers').doc(layer.id);
            deletes.push(layerRef.delete());

            // Notes List A5 (19 Oct 2026): a cue.deleted for each of the layer's cues, then
            // layer.deleted. Before this, those cues reached Notes List only on the next full sync.
            Promise.all(deletes).then(() => {
                layerCues.forEach(cue => dispatch('queueNotesListStructureEvent', { event: 'cue.deleted', cueId: cue.id }).catch(() => {}));
                return dispatch('queueNotesListStructureEvent', { event: 'layer.deleted', layerId: layer.id });
            }).catch(() => {});

            dispatch('hideLayer', layer);
            dispatch('hideNotes', layer);
            dispatch('removeLayerFromCollaborators', layer);
        } catch (e) {
            console.error(e);
        }
    },
    setEditingLayer({ getters }, {layer, type}) {
        try {
            let fieldKey = 'projects.' + getters.project.id + '.layers.editing';
            if (type === 'note') {
                fieldKey = 'projects.' + getters.project.id + '.layers.noting';
            }
            if (type === 'cue') {
                fieldKey = 'projects.' + getters.project.id + '.layers.cueing';
            }
            repos.users.update(getters.user.uid, {
                [fieldKey]: layer.id
            });
        } catch (e) {
            console.error(e);
        }
    },
    setLayerTitleFontSize({ getters }, {layer, titleFontSize}) {
        try {
            repos.users.update(getters.user.uid, {
                ['projects.' + getters.project.id + '.layers.' + layer.id + '.titleFontSize']: titleFontSize
            });
        } catch (e) {
            console.error(e);
        }
    },
    setDefaultCuePlacement({ getters }, {layer, defaultCuePlacement}) {
        try {
            repos.users.update(getters.user.uid, {
                ['projects.' + getters.project.id + '.layers.' + layer.id + '.defaultCuePlacement']: defaultCuePlacement
            });
        } catch (e) {
            console.error(e);
        }
    },
    setDefaultTextStampBackground({ getters }, {layer, defaultTextStampBackground}) {
        try {
            const batch = new Batch();
            batch.update(db.collection('projects').doc(getters.project.id).collection('layers').doc(layer.id), {
                defaultTextStampBackground: defaultTextStampBackground
            });
            batch.commit();
        } catch (e) {
            console.error(e);
        }
    },
    setLayerBoxOpacity({ getters }, {layer, boxOpacity}) {
        try {
            repos.users.update(getters.user.uid, {
                ['projects.' + getters.project.id + '.layers.' + layer.id + '.boxOpacity']: boxOpacity
            });
        } catch (e) {
            console.error(e);
        }
    },
    setLayerLineOpacity({ getters }, {layer, lineOpacity}) {
        try {
            repos.users.update(getters.user.uid, {
                ['projects.' + getters.project.id + '.layers.' + layer.id + '.lineOpacity']: lineOpacity
            });
        } catch (e) {
            console.error(e);
        }
    },
    setLayerHeaderBox({ getters }, {layer, headerBox}) {
        try {
            repos.users.update(getters.user.uid, {
                ['projects.' + getters.project.id + '.layers.' + layer.id + '.headerBox']: headerBox
            });
        } catch (e) {
            console.error(e);
        }
    },
    setLayerAutoNumbering({ getters }, {layer, autoNumbering}) {
        try {
            repos.users.update(getters.user.uid, {
                ['projects.' + getters.project.id + '.layers.' + layer.id + '.autoNumbering']: autoNumbering
            });
        } catch (e) {
            console.error(e);
        }
    },
    setLayerAutoNumberStart({ getters }, {layer, autoNumberStart}) {
        try {
            repos.users.update(getters.user.uid, {
                ['projects.' + getters.project.id + '.layers.' + layer.id + '.autoNumberStart']: autoNumberStart
            });
        } catch (e) {
            console.error(e);
        }
    },
    setLayerAutoNumberInterval({ getters }, {layer, autoNumberInterval}) {
        try {
            repos.users.update(getters.user.uid, {
                ['projects.' + getters.project.id + '.layers.' + layer.id + '.autoNumberInterval']: autoNumberInterval
            });
        } catch (e) {
            console.error(e);
        }
    },
    setLayerUserDefined({ getters }, {layer, userDefined}) {
        try {
            db.doc(getters.projectPath).collection('layers').doc(layer.id).update({
                userDefined: userDefined
            });
        } catch (e) {
            console.error(e);
        }
    },
    setLayerAttribute({ getters }, {layer, attribute}) {
        return new Promise((resolve, reject) => {
            (async () => {
                try {
                    await db.doc(getters.projectPath).collection('layers').doc(layer.id).update({
                        ['attributes.' + attribute.key]: attribute.value
                    });
                    resolve();
                } catch (e) {
                    console.error(e);
                    reject(e);
                }
            })();
        });
    },
    updateLayerAttributes({ getters }, {layer, attributes}) {
        return new Promise((resolve, reject) => {
            (async () => {
                try {
                    await db.doc(getters.projectPath).collection('layers').doc(layer.id).update({
                        attributes
                    });
                    resolve();
                } catch (e) {
                    console.error(e);
                    reject(e);
                }
            })();
        });
    },
    showListLayer({ getters }, layer) {
        try {
            repos.users.update(getters.user.uid, {
                ['projects.' + getters.project.id + '.layers.listViewing']: firebase.firestore.FieldValue.arrayUnion(layer.id)
            });
        } catch (e) {
            console.error(e);
        }
    },
    addLayerToCollaborators({ getters }, layer) {
        try {
            for (const collab of getters.collaborators) {
                repos.users.updateOther(collab.uid, {
                    ['projects.' + getters.project.id + '.layers.viewing']: firebase.firestore.FieldValue.arrayUnion(layer.id),
                    ['projects.' + getters.project.id + '.layers.listViewing']: firebase.firestore.FieldValue.arrayUnion(layer.id),
                    ['projects.' + getters.project.id + '.layers.showNotes']: firebase.firestore.FieldValue.arrayUnion(layer.id),
                    ['projects.' + getters.project.id + '.layers.showCues']: firebase.firestore.FieldValue.arrayUnion(layer.id)
                });
            }
        } catch (e) {
            console.error(e);
        }
    },
    removeLayerFromCollaborators({ getters }, layer) {
        try {
            for (const collab of getters.collaborators) {
                repos.users.updateOther(collab.uid, {
                    ['projects.' + getters.project.id + '.layers.viewing']: firebase.firestore.FieldValue.arrayRemove(layer.id),
                    ['projects.' + getters.project.id + '.layers.listViewing']: firebase.firestore.FieldValue.arrayRemove(layer.id),
                    ['projects.' + getters.project.id + '.layers.showNotes']: firebase.firestore.FieldValue.arrayRemove(layer.id),
                    ['projects.' + getters.project.id + '.layers.showCues']: firebase.firestore.FieldValue.arrayRemove(layer.id)
                });
            }
        } catch (e) {
            console.error(e);
        }
    },
    showAllLayers({ getters }) {
        try {
            repos.users.update(getters.user.uid, {
                ['projects.' + getters.project.id + '.layers.viewing']: getters.visibleLayerIDs
            });
        } catch (e) {
            console.error(e);
        }
    },
    showAllProjectLayers({ getters }, { project, layers }) {
        try {
            repos.users.update(getters.user.uid, {
                ['projects.' + project.id + '.layers.viewing']: layers.map(layer => layer.id)
            });
        } catch (e) {
            console.error(e);
        }
    },
    showLayer({ getters }, layer) {
        try {
            repos.users.update(getters.user.uid, {
                ['projects.' + getters.project.id + '.layers.viewing']: firebase.firestore.FieldValue.arrayUnion(layer.id),
                ['projects.' + getters.project.id + '.layers.showCues']: firebase.firestore.FieldValue.arrayUnion(layer.id),
                ['projects.' + getters.project.id + '.layers.showNotes']: firebase.firestore.FieldValue.arrayUnion(layer.id)
            });
        } catch (e) {
            console.error(e);
        }
    },
    showLayerAll({ getters }, layerAll) {
        try {
            repos.users.update(getters.user.uid, {
                ['projects.' + getters.project.id + '.layers.viewing']: layerAll
            });
        } catch (e) {
            console.error(e);
        }
    },
    hideLayer({ getters }, layer) {
        try {
            repos.users.update(getters.user.uid, {
                ['projects.' + getters.project.id + '.layers.viewing']: firebase.firestore.FieldValue.arrayRemove(layer.id),
                ['projects.' + getters.project.id + '.layers.showCues']: firebase.firestore.FieldValue.arrayRemove(layer.id),
                ['projects.' + getters.project.id + '.layers.showNotes']: firebase.firestore.FieldValue.arrayRemove(layer.id)
            });
        } catch (e) {
            console.error(e);
        }
    },
    // Split view's own layer choice: always writes the whole array (the first click seeds it).
    setSplitLayers({ getters }, layerIDs) {
        try {
            repos.users.update(getters.user.uid, {
                ['projects.' + getters.project.id + '.layers.splitViewing']: layerIDs
            });
        } catch (e) {
            console.error(e);
        }
    },
    // A layer the person creates switches on in their split list, but only once they have a
    // saved choice: while following the script it arrives through layers.viewing, and an
    // arrayUnion on the absent field would leave only the new layer shown.
    showSplitLayer({ getters }, layer) {
        try {
            const data = getters.userProjectData;
            if (!addsNewLayerToSplit(data && data.layers)) return;
            repos.users.update(getters.user.uid, {
                ['projects.' + getters.project.id + '.layers.splitViewing']: firebase.firestore.FieldValue.arrayUnion(layer.id)
            });
        } catch (e) {
            console.error(e);
        }
    },
    hideListLayer({ getters }, layer) {
        try {
            repos.users.update(getters.user.uid, {
                ['projects.' + getters.project.id + '.layers.listViewing']: firebase.firestore.FieldValue.arrayRemove(layer.id)
            });
        } catch (e) {
            console.error(e);
        }
    },
    hideAllLayers({ getters }) {
        try {
            repos.users.update(getters.user.uid, {
                ['projects.' + getters.project.id + '.layers.viewing']: firebase.firestore.FieldValue.delete()
            });
        } catch (e) {
            console.error(e);
        }
    },
    hideAllProjectLayers({ getters }, project) {
        try {
            repos.users.update(getters.user.uid, {
                ['projects.' + project.id + '.layers.viewing']: firebase.firestore.FieldValue.delete()
            });
        } catch (e) {
            console.error(e);
        }
    },
    defaultShowCues({ getters }, layer) {
        try {
            repos.users.update(getters.user.uid, {
                ['projects.' + getters.project.id + '.layers.showCues']: layer
            });
        } catch (e) {
            console.error(e);
        }
    },
    showCues({ getters }, layer) {
        try {
            repos.users.update(getters.user.uid, {
                ['projects.' + getters.project.id + '.layers.showCues']: firebase.firestore.FieldValue.arrayUnion(layer.id)
            });
        } catch (e) {
            console.error(e);
        }
    },
    hideCues({ getters }, layer) {
        try {
            repos.users.update(getters.user.uid, {
                ['projects.' + getters.project.id + '.layers.showCues']: firebase.firestore.FieldValue.arrayRemove(layer.id)
            });
        } catch (e) {
            console.error(e);
        }
    },
    showNotes({ getters }, layer) {
        try {
            repos.users.update(getters.user.uid, {
                ['projects.' + getters.project.id + '.layers.showNotes']: firebase.firestore.FieldValue.arrayUnion(layer.id)
            });
        } catch (e) {
            console.error(e);
        }
    },
    hideNotes({ getters }, layer) {
        try {
            repos.users.update(getters.user.uid, {
                ['projects.' + getters.project.id + '.layers.showNotes']: firebase.firestore.FieldValue.arrayRemove(layer.id)
            });
        } catch (e) {
            console.error(e);
        }
    },

    // CUE FUNCTIONS
    addCue({ getters, commit, dispatch }, cue) {
        // The new cue auto-focuses its title field a beat later; suppress tool
        // shortcuts so a keystroke in that gap types instead of firing a shortcut.
        suppressShortcuts();
        return new Promise((resolve, reject) => {
            (async () => {
                try {
                    const projectRef = db.doc(getters.projectPath);
                    const newCueRef = projectRef.collection('cues').doc();
                    cue.id = newCueRef.id;
                    cue.layer = getters.currentCueLayer.id;
                    cue.createdAt = firebase.firestore.FieldValue.serverTimestamp();
                    cue.editedAt = firebase.firestore.FieldValue.serverTimestamp();

                    // Auto-numbering: if the creating user has auto-numbering enabled
                    // for this layer and the cue has no title yet, stamp the layer's
                    // stored "next number" and advance it by the interval so the panel
                    // field shows the upcoming number. The stored value is per-user and
                    // also editable in the panel. Skipped while viewing a saved version.
                    // (Truly-simultaneous creates could still collide on the stored
                    // value; transaction-hardening is tracked as a follow-up.)
                    let autoNumbered = false;
                    // ⚠ Set when auto-numbering was ON but could not run. Until
                    // 14 Sept 2026 this branch had no `else` at all: the cue was
                    // created with an empty number and NOTHING anywhere said why.
                    // The layer editor's warning is the right message in the wrong
                    // place -- that panel is shut by the time cues are being added.
                    // The caller toasts this; see autoNumberWarning.js.
                    let autoNumberSkipped = null;
                    if ((cue.title == null || cue.title === '') && !getters.currentVersion &&
                        getters.getLayerAutoNumbering(cue.layer)) {
                        const next = getters.getLayerAutoNumberStart(cue.layer);
                        if (next != null && Number.isFinite(Number(next))) {
                            cue.title = String(next);
                            autoNumbered = true;
                            const interval = getters.getLayerAutoNumberInterval(cue.layer);
                            dispatch('setLayerAutoNumberStart', {
                                layer: { id: cue.layer },
                                autoNumberStart: Utils.roundCueNumber(Number(next) + interval)
                            });
                        } else {
                            autoNumberSkipped = 'no-seed';
                        }
                    }

                    if (!cue.data) {
                        cue.data = {...Object.keys(Utils.cueAttributes).reduce((attrs, attr) => ({...attrs, [attr]: ''}), {}), label: '', partsData: [], followHangType: ''};
                    }
                    await newCueRef.set(cue);
                    dispatch('queueNotesListStructureEvent', {
                        event: 'cue.created',
                        cueId: cue.id
                    });
                    // autoNumbered tells the caller the number field was stamped, so
                    // focus can skip it and land on the next field instead.
                    resolve({
                        id: cue.id,
                        autoNumbered,
                        autoNumberSkipped,
                        layerID: cue.layer
                    });
                } catch (e) {
                    console.error(e);
                    reject(e);
                }
            })();
        });
    },
    deleteCue({ getters, dispatch }, cue) {
        try {
            const projectRef = db.doc(getters.projectPath);
            projectRef.collection('cues').doc(cue.id).delete().then(() => {
                dispatch('queueNotesListStructureEvent', {
                    event: 'cue.deleted',
                    cueId: cue.id
                });
            });
            dispatch('removeCueFloating', {
                id: cue.id
            });
            dispatch('removeCueHeaderBoxOptions', {
                id: cue.id
            });
            dispatch('removeCueUserDefinedOptions', {
                id: cue.id
            });
            dispatch('removeCueTitleFontSize', {
                id: cue.id
            });
        } catch (e) {
            console.error(e);
        }
    },
    // Bulk-delete cues (list-view "Delete Selected Cues"). Mirrors deleteCue's
    // per-cue option cleanup for each cue, and returns a promise resolving once
    // every cue doc delete has committed so the caller can clear the selection.
    bulkDeleteCues({ getters, dispatch }, cues) {
        const projectRef = db.doc(getters.projectPath);
        const deletes = [];
        for (const cue of cues) {
            deletes.push(projectRef.collection('cues').doc(cue.id).delete());
            dispatch('removeCueFloating', { id: cue.id });
            dispatch('removeCueHeaderBoxOptions', { id: cue.id });
            dispatch('removeCueUserDefinedOptions', { id: cue.id });
            dispatch('removeCueTitleFontSize', { id: cue.id });
        }
        return Promise.all(deletes);
    },
    updateCue({ getters, commit, dispatch }, cue) {
        // A cue saved to a page that is not a whole number >= 1 vanishes: nothing renders
        // pageNumber NaN. Two cues were lost that way on 1 Oct 2026 (see lib/cue-page.js).
        const pageCheck = checkCuePageWrite(cue);
        if (!pageCheck.ok) {
            return Promise.reject(new Error(pageCheck.reason));
        }
        const projectRef = db.doc(getters.projectPath);
        // The cue boxes pass the copy the page draws; its line/offset/height fields are
        // display-only and must not be saved (lib/cue-drawing-fields.js).
        return projectRef.collection('cues').doc(cue.id).update(withoutDrawingFields(cue)).then(() => dispatch('queueNotesListStructureEvent', {
            event: 'cue.updated',
            cueId: cue.id
        }));
    },
    // Bulk cue update via a chunked batch — same per-cue write as updateCue, but
    // batched. Page-ops (replace/insert/delete) shift pageNumber on every cue after
    // the change point; on a dense project that was ~1,000+ individual updateCue
    // writes fired at once, which made the op crawl/hang. The Batch helper auto-flushes
    // at 490 ops. Returns the commit promise so callers can await completion.
    bulkUpdateCues({ getters }, cues) {
        const projectRef = db.doc(getters.projectPath);
        const batch = new Batch();
        for (const cue of cues) {
            const pageCheck = checkCuePageWrite(cue);
            if (!pageCheck.ok) {
                return Promise.reject(new Error(pageCheck.reason));
            }
        }
        for (const cue of cues) {
            batch.update(projectRef.collection('cues').doc(cue.id), withoutDrawingFields(cue));
        }
        return batch.commit();
    },
    // Bulk-set cue titles (used by list-view auto-numbering + its undo).
    // updates: [{ id, title }]. Writes title + a fresh editedAt via a chunked
    // batch. Returns the commit promise so callers can await completion.
    bulkSetCueTitles({ getters }, updates) {
        const projectRef = db.doc(getters.projectPath);
        const batch = new Batch();
        for (const update of updates) {
            batch.update(projectRef.collection('cues').doc(update.id), {
                title: update.title,
                editedAt: firebase.firestore.FieldValue.serverTimestamp()
            });
        }
        return batch.commit();
    },
    // Bulk-apply pasted cell values (list-view spreadsheet paste). edits:
    // [{ cueId, attr, part, value }] where part!=null targets partsData[part][attr],
    // attr==='title' targets the cue title, else data[attr]. Groups edits per cue,
    // applies only real changes (no-op writes are skipped), and commits one chunked
    // batch. Reads current cue data from the store so no per-cell Firestore reads.
    bulkUpdateCueCells({ getters }, edits) {
        const projectRef = db.doc(getters.projectPath);
        const cues = getters.cues || [];
        const byCue = new Map();
        for (const edit of edits) {
            if (!byCue.has(edit.cueId)) byCue.set(edit.cueId, []);
            byCue.get(edit.cueId).push(edit);
        }
        const batch = new Batch();
        let wrote = 0;
        for (const [cueId, cueEdits] of byCue) {
            const cue = cues.find(c => c.id === cueId);
            if (cue) {
                const data = JSON.parse(JSON.stringify(cue.data || {}));
                const update = {};
                let changed = false;
                for (const edit of cueEdits) {
                    const { attr, part, value } = edit;
                    const val = value == null ? '' : String(value);
                    if (attr === 'title') {
                        if ((cue.title == null ? '' : String(cue.title)) !== val) {
                            update.title = val;
                            changed = true;
                        }
                    } else if (part != null) {
                        const partRow = Array.isArray(data.partsData) ? data.partsData[part] : null;
                        if (partRow) {
                            const cur = partRow[attr];
                            if ((cur == null ? '' : String(cur)) !== val) {
                                partRow[attr] = val;
                                update.data = data;
                                changed = true;
                            }
                        }
                    } else {
                        const cur = data[attr];
                        if ((cur == null ? '' : String(cur)) !== val) {
                            data[attr] = val;
                            update.data = data;
                            changed = true;
                        }
                    }
                }
                if (changed) {
                    update.editedAt = firebase.firestore.FieldValue.serverTimestamp();
                    batch.update(projectRef.collection('cues').doc(cueId), update);
                    wrote += 1;
                }
            }
        }
        if (!wrote) return Promise.resolve();
        return batch.commit();
    },
    async modifyCue({ getters, dispatch }, { id, data }) {
        const projectRef = db.doc(getters.projectPath);
        const cueRef = projectRef.collection('cues').doc(id);
        const cueDoc = await cueRef.get();
        const cueData = cueDoc.data();
        const shouldNotifyStructure = cueWriteNotifiesNotesList(data);
        return projectRef.collection('cues').doc(id).update({
            ...data,
            editedAt: cueData.editedAt || firebase.firestore.FieldValue.serverTimestamp()
        }).then(() => {
            if (shouldNotifyStructure) {
                dispatch('queueNotesListStructureEvent', {
                    event: 'cue.updated',
                    cueId: id
                });
            }
        });
    },
    removeCueProperties({ getters}, {cue, keys}) {
        const projectRef = db.doc(getters.projectPath);
        return projectRef.collection('cues').doc(cue.id).update(
            keys.reduce((map, key) => {
                map[key] = firebase.firestore.FieldValue.delete();
                return map;
            }, {})
        );
    },
    deleteCueWorkNote({ getters }, cue) {
        try {
            const projectRef = db.doc(getters.projectPath);
            projectRef.collection('cues').doc(cue.id).update({
                'data.workNote': ''
            });
        } catch (e) {
            console.error(e);
        }
    },
    addFloatingCue({ state, getters }, {id, left, top}) {
        repos.users.update(getters.user.uid, {
            ['projects.' + getters.project.id + '.floatingCues.' + id]: {
                floating: true,
                left,
                top
            }
        });
    },
    removeCueFloating({ state, getters}, {id}) {
        repos.users.update(getters.user.uid, {
            ['projects.' + getters.project.id + '.floatingCues.' + id]: firebase.firestore.FieldValue.delete()
        });
    },
    addCueHeaderBoxOptions({ state, getters }, {id, title, label, time, layerName}) {
        // Persist ONLY the keys explicitly provided, so a per-cue toggle pins just that one
        // option and every other option keeps following the layer's global default. (The
        // "at least one of title/label on" guard is enforced by the per-cue menu handlers.)
        const base = 'projects.' + getters.project.id + '.headerBoxOptions.' + id;
        const update = {};
        if (title !== undefined) { update[base + '.title'] = title; }
        if (label !== undefined) { update[base + '.label'] = label; }
        if (time !== undefined) { update[base + '.time'] = time; }
        if (layerName !== undefined) { update[base + '.layerName'] = layerName; }
        if (Object.keys(update).length) {
            repos.users.update(getters.user.uid, update);
        }
    },
    addCueUserDefinedOptions({ state, getters }, {id, title = true, label = false}) {
        if (!title && !label) {
            title = true;
        }
        repos.users.update(getters.user.uid, {
            ['projects.' + getters.project.id + '.userDefinedOptions.' + id]: {
                title,
                label
            }
        });
    },
    removeCueHeaderBoxOptions({ state, getters}, {id}) {
        repos.users.update(getters.user.uid, {
            ['projects.' + getters.project.id + '.headerBoxOptions.' + id]: firebase.firestore.FieldValue.delete()
        });
    },
    removeCueUserDefinedOptions({ state, getters}, {id}) {
        repos.users.update(getters.user.uid, {
            ['projects.' + getters.project.id + '.userDefinedOptions.' + id]: firebase.firestore.FieldValue.delete()
        });
    },
    // Cue paste: the paster's own look for the copied cue, onto the new cue, in one write to their own user doc
    // (keys built by lib/cue-paste.js userOptionsForPaste).
    copyCueLook({ getters }, update) {
        return repos.users.update(getters.user.uid, update);
    },
    addCueTitleFontSize({ state, getters }, {id, titleFontSize}) {
        repos.users.update(getters.user.uid, {
            ['projects.' + getters.project.id + '.titleFontSizeOptions.' + id]: titleFontSize
        });
    },
    removeCueTitleFontSize({ state, getters}, {id}) {
        repos.users.update(getters.user.uid, {
            ['projects.' + getters.project.id + '.titleFontSizeOptions.' + id]: firebase.firestore.FieldValue.delete()
        });
    },
    addCueLineOpacity({ state, getters }, {id, lineOpacity}) {
        repos.users.update(getters.user.uid, {
            ['projects.' + getters.project.id + '.cueLineOpacityOptions.' + id]: lineOpacity
        });
    },
    removeCueLineOpacity({ state, getters}, {id}) {
        repos.users.update(getters.user.uid, {
            ['projects.' + getters.project.id + '.cueLineOpacityOptions.' + id]: firebase.firestore.FieldValue.delete()
        });
    },
    addCueBoxOpacity({ state, getters }, {id, boxOpacity}) {
        repos.users.update(getters.user.uid, {
            ['projects.' + getters.project.id + '.cueBoxOpacityOptions.' + id]: boxOpacity
        });
    },
    removeCueBoxOpacity({ state, getters}, {id}) {
        repos.users.update(getters.user.uid, {
            ['projects.' + getters.project.id + '.cueBoxOpacityOptions.' + id]: firebase.firestore.FieldValue.delete()
        });
    },


    useVersion({ state, getters, dispatch }, {versionID, callback}) {
        if (versionID) {
            dispatch('setActiveVersion', {
                ref: db.collection('projects').doc(getters.project.id).collection('versions').doc(versionID),
                callback
            });
        } else if (getters.currentVersion) {
            dispatch('unsetActiveVersion', {
                ref: db.collection('projects').doc(getters.project.id).collection('versions').doc(getters.currentVersion.id),
                callback
            });
        }
    },
    // Rename a saved history version. Auto-saved (pre-page-op) versions only get a
    // date/time name; this lets the user give any version a real name, like a layer.
    // Renaming also clears `autoSave` (null, not deleted: see classifyVersion), so an
    // automatic save the user names leaves its folder and is never pruned.
    renameVersion({ getters }, { versionID, name }) {
        return db.collection('projects').doc(getters.project.id)
            .collection('versions').doc(versionID)
            .update({ name, autoSave: null });
    },
    // `version` is the name (a user's save), or { name, autoSave, autoSaveDetail }
    // for an automatic one (history-autosave.js). Resolves the new version's id.
    //
    // Shows switched on (project.serverVersionSave, or project.serverPageChange; see
    // lib/server-switches.js) get the SERVER copy (functions
    // createVersionSnapshot, 14 Oct 2026): one consistent read of the project and its
    // cues, notes, layers and structure markers (#386), the version doc written last with
    // `structureSnapshot: true`. Same doc ids and fields as the browser copy. Every other
    // show keeps the browser copy below, unchanged, until Josh switches it on.
    //
    // Awaited, never in the background: the three page-op modals and promoteVersion
    // call this BEFORE they change anything, and the copy must be the before-state.
    // On failure this rejects and each caller keeps its own path (page ops: error toast,
    // "Proceed Without Saving" still offered; promote: aborts, fail-closed).
    createVersion({ getters, dispatch }, version) {
        const project = getters.project;
        if (!usesServerVersionSave(project)) return dispatch('createVersionInBrowser', version);
        return saveVersionOnServer({
            db,
            callable: firebase.functions().httpsCallable('createVersionSnapshot'),
            projectId: project && project.id,
            version
        }).catch((error) => {
            console.error('Error creating version:', error);
            throw error;
        });
    },
    // The browser copy (today's path for every show not switched on above).
    createVersionInBrowser({ getters }, version) {
        const versionName = typeof version === 'string' ? version : version.name;
        const autoFields = typeof version === 'string' || !version.autoSave ? {} : {
            autoSave: version.autoSave,
            autoSaveDetail: version.autoSaveDetail || ''
        };
        return new Promise((resolve, reject) => {
            try {
                const project = getters.project;
                const userUID = getters.user.uid;
                const now = new Date();
                const newVersionRef = db.collection('projects').doc(project.id).collection('versions').doc();

                // ReDoS-safe regex patterns: avoid nested quantifiers and catastrophic backtracking
                // Add input length validation as defense-in-depth
                if (!project.documentUrl || project.documentUrl.length > 2048) {
                    reject(new Error('Invalid document URL: URL too long or missing'));
                    return;
                }
                
                // Use more restrictive patterns to prevent backtracking attacks
                // Firebase Storage URL format: https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{path}?alt=media&token={token}
                let regexp = /^https?:\/\/firebasestorage\.googleapis\.com\/v0\/b\/[^/]+\/o\/([^?]+)\?alt=media&token=[^&]+$/;
                if (project.issrc) {
                    // For src projects, use GoogleAccessId format
                    regexp = /^https?:\/\/[^/]+\/([^?]+)\?GoogleAccessId=[^&]+$/;
                }
                const matches = regexp.exec(project.documentUrl);

                if (!matches || !matches[1]) {
                    reject(new Error('Invalid document URL: Could not parse file path'));
                    return;
                }

                Vue.http.post(PROJECT_CREATE_VERSION_URL, {
                    project_id: project.id,
                    documentUrl: project.documentUrl,
                    version_id: newVersionRef.id,
                    filePath: decodeURIComponent(matches[1])
                })
                .then(() => {
                    const storage = firebase.storage();
                    return storage.ref('pdfs/' + project.id + '/' + newVersionRef.id + '.pdf').getDownloadURL();
                })
                .then((documentUrl) => {
                    const batch = new Batch();
                    batch.set(newVersionRef, Object.assign({
                        id: newVersionRef.id,
                        created: now.toISOString(),
                        name: versionName,
                        creator: userUID,
                        documentUrl,
                        pdfPageNumbers: project.pdfPageNumbers,
                        // Records that THIS code wrote the version, so an empty
                        // scriptMarkers subcollection can be read as "the project
                        // genuinely had no structure" rather than "structure was
                        // never captured". Counting marker documents cannot tell
                        // those apart, and they want opposite behaviour on
                        // promotion: roll structure back, versus keep the live
                        // marks because the version's were never recorded.
                        //
                        // Absent means pre-#386. Never backfill it onto existing
                        // versions: for a version saved before the fix the claim
                        // would be false, and promoting it would then delete
                        // structure it never held a copy of.
                        structureSnapshot: true
                    }, autoFields));

                    const cuesCollectionRef = newVersionRef.collection('cues');
                    for (const cue of Object.values(project.cues || {})) {
                        batch.set(cuesCollectionRef.doc(cue.id), cue);
                    }
                    const notesCollectionRef = newVersionRef.collection('notes');
                    for (const note of Object.values(project.notes || {})) {
                        batch.set(notesCollectionRef.doc(note.id), note);
                    }
                    const layersCollectionRef = newVersionRef.collection('layers');
                    for (const layer of Object.values(project.layers || {})) {
                        batch.set(layersCollectionRef.doc(layer.id), layer);
                    }
                    // Structure (Act/Scene/Song) markers — #386. Versions handled
                    // exactly three subcollections and never this one, so opening
                    // an old version showed TODAY's structure over that version's
                    // cues.
                    //
                    // The three page-op modals all call createVersion BEFORE
                    // running the operation, and the marker shift happens inside
                    // that operation, so what is captured here is correctly the
                    // pre-operation position. Do not move this call after a page
                    // op without re-reading that.
                    const markersCollectionRef = newVersionRef.collection('scriptMarkers');
                    for (const marker of Object.values(project.scriptMarkers || {})) {
                        batch.set(markersCollectionRef.doc(marker.markerId || marker.id), marker);
                    }
                    return batch.commit();
                })
                .then(() => {
                    resolve(newVersionRef.id);
                })
                .catch((error) => {
                    console.error('Error creating version:', error);
                    reject(error);
                });
            } catch (e) {
                console.error('Error in createVersion:', e);
                reject(e);
            }
        });
    },
    // A page change renumbered on the SERVER (functions applyPageChange, 14 Oct 2026; lib/server-page-change.js),
    // for shows with serverPageChange === true. Call it after the PDF helper has changed the PDF. The server moves
    // every cue, note and Act/Scene mark, writes the labels and the reload signal, and lifts the lock last; this
    // then moves this user's bookmarks, clears this user's options for cues that went with deleted pages (one write
    // instead of four per cue), and sends Notes List one structure notice. Rejects if the server refused or failed twice.
    async applyPageChangeOnServer({ getters, dispatch }, { op, params, pagesBefore }) {
        const project = getters.project;
        const result = await renumberPagesOnServer({
            db,
            callable: firebase.functions().httpsCallable('applyPageChange', { timeout: 300000 }),
            projectId: project.id,
            opId: db.collection('pageOps').doc().id,
            op,
            params,
            pagesBefore
        });
        const { moves, removes } = bookmarkChanges(op, params, getters.bookmarks);
        await Promise.all([
            dispatch('moveBookmarksBulk', { moves, removes }),
            dispatch('removeCueUserOptionsBulk', result.deletedCueIds || [])
        ]);
        dispatch('notifyNotesListStructureIfLinked');
        return result;
    },
    // removeCueFloating + removeCueHeaderBoxOptions + removeCueUserDefinedOptions + removeCueTitleFontSize for
    // many cues, in as few user-record writes as possible (400 field deletes each).
    removeCueUserOptionsBulk({ getters }, ids) {
        if (!ids || !ids.length || !getters.user || !getters.user.uid) return Promise.resolve();
        const base = 'projects.' + getters.project.id + '.';
        const maps = ['floatingCues', 'headerBoxOptions', 'userDefinedOptions', 'titleFontSizeOptions'];
        const writes = [];
        for (let i = 0; i < ids.length; i += 100) {
            const payload = {};
            for (const id of ids.slice(i, i + 100)) {
                for (const m of maps) payload[base + m + '.' + id] = firebase.firestore.FieldValue.delete();
            }
            writes.push(repos.users.update(getters.user.uid, payload));
        }
        return Promise.all(writes);
    },
    // Keep the newest AUTO_SAVE_CAP automatic saves of `kind` on a project stamped
    // `autoSaveCap` at creation; older projects are never pruned. Runs in the app
    // right after an automatic save, through deleteVersion (which also removes the
    // PDF from Storage). Reads the versions fresh rather than from the binding, which
    // may not have caught up with the save just made. Never throws: a failed prune
    // must not fail the page change or make-live that triggered it.
    async pruneAutoSaves({ getters, dispatch }, { kind, protectIds }) {
        try {
            const project = getters.project;
            if (!project || project.autoSaveCap !== true) return [];
            const snapshot = await db.collection('projects').doc(project.id).collection('versions').get();
            const versions = snapshot.docs.map(d => Object.assign({ id: d.id }, d.data()));
            const protect = (protectIds || []).slice();
            if (getters.currentVersion) protect.push(getters.currentVersion.id);
            const ids = selectPrune(versions, kind, { capEnabled: true, protectIds: protect });
            // One at a time, oldest first (normally just one).
            const known = ids.filter(id => getters.versionByID(id));
            await known.reduce((chain, id) => chain.then(() => dispatch('deleteVersion', id)), Promise.resolve());
            return known;
        } catch (e) {
            console.error('pruneAutoSaves:', e);
            return [];
        }
    },
    deleteVersion({ state, getters, dispatch }, versionID) {
        return new Promise((resolve, reject) => {
            (async () => {
                try {
                    if (state.activeVersion === versionID) state.activeVersion = null;

                    const projectID = getters.project.id;
                    const version = getters.versionByID(versionID);
                    if (!version) {
                        reject(new Error('Version not found'));
                        return;
                    }
                    const versionRef = db.collection('projects').doc(projectID).collection('versions').doc(versionID);

                    let redirect = false;
                    if (getters.currentVersion && getters.currentVersion.id === versionID) {
                        await dispatch('unsetActiveVersion', {
                            ref: versionRef
                        });
                        redirect = true;
                    }
                    
                    // Get all subcollections. scriptMarkers added for #386; on a
                    // version saved before that fix the collection is simply
                    // absent and .get() returns an empty snapshot.
                    const [cuesSnapshot, notesSnapshot, layersSnapshot, markersSnapshot] = await Promise.all([
                        versionRef.collection('cues').get(),
                        versionRef.collection('notes').get(),
                        versionRef.collection('layers').get(),
                        versionRef.collection('scriptMarkers').get()
                    ]);
                    
                    const batch = db.batch();
                    
                    // Delete all subcollection documents
                    for (const cueDoc of cuesSnapshot.docs) {
                        batch.delete(cueDoc.ref);
                    }
                    for (const noteDoc of notesSnapshot.docs) {
                        batch.delete(noteDoc.ref);
                    }
                    for (const layerDoc of layersSnapshot.docs) {
                        batch.delete(layerDoc.ref);
                    }
                    for (const markerDoc of markersSnapshot.docs) {
                        batch.delete(markerDoc.ref);
                    }
                    
                    // Delete the version document itself
                    batch.delete(versionRef);
                    
                    // Delete PDF if exists
                    if (version.documentUrl) {
                        try {
                            const storage = firebase.storage();
                            const pdfRef = storage.ref('pdfs/' + projectID + '/' + versionID + '.pdf');
                            await pdfRef.delete();
                        } catch (err) {
                            console.error('Error deleting PDF:', err);
                        }
                    }
                    
                    // Commit the batch
                    await batch.commit();
                    
                    if (redirect) {
                        router.replace(`/project/${projectID}`);
                    }
                    
                    resolve();
                } catch (err) {
                    console.error('Error in deleteVersion:', err);
                    reject(err);
                }
            })();
        });
    },
    async promoteVersion({ getters, dispatch }, versionID) {
        try {
            const project = getters.project;
            const userUID = getters.user.uid;
            const ownerUID = getters.ownerUID;
            const currentVersion = getters.currentVersion;
            const version = getters.versionByID(versionID);

            if (userUID !== ownerUID) {
                throw new Error('Only project owner can promote versions');
            }

            if (!version) {
                throw new Error('Version not found');
            }

            const projectRef = db.collection('projects').doc(project.id);
            const versionRef = projectRef.collection('versions').doc(versionID);

            if (!version.documentUrl) {
                throw new Error('Version document URL is missing');
            }

            const projectMatches = /^https?:\/\/firebasestorage\.googleapis\.com\/v0\/b\/[^/]+\/o\/([^?]+)\?alt=media&token=[^&]+$/.exec(project.documentUrl);
            const versionMatches = /^https?:\/\/firebasestorage\.googleapis\.com\/v0\/b\/[^/]+\/o\/([^?]+)\?alt=media&token=[^&]+$/.exec(version.documentUrl);

            if (!projectMatches || !versionMatches) {
                throw new Error('Invalid document URL format');
            }

            // Fetch all required data
            const [
                projectCuesSnapshot,
                projectNotesSnapshot,
                projectLayersSnapshot,
                versionCuesSnapshot,
                versionNotesSnapshot,
                versionLayersSnapshot,
                projectMarkersSnapshot,
                versionMarkersSnapshot
            ] = await Promise.all([
                projectRef.collection('cues').get(),
                projectRef.collection('notes').get(),
                projectRef.collection('layers').get(),
                versionRef.collection('cues').get(),
                versionRef.collection('notes').get(),
                versionRef.collection('layers').get(),
                projectRef.collection('scriptMarkers').get(),
                versionRef.collection('scriptMarkers').get()
            ]);
            const versionDoc = await versionRef.get();
            // Versions saved by the legacy app can have no page list (21 Oct 2026). Writing
            // undefined threw AFTER the PDF was replaced; [] is rebuilt 1..N on the next load.
            const pdfPageNumbers = labelsToRestore(versionDoc.data().pdfPageNumbers);

            // Snapshot the CURRENT live state before overwriting it (23 Aug 2026).
            //
            // Promotion replaces the PDF, the page numbering, and every cue, note,
            // layer and structure mark — and until now saved none of it anywhere,
            // which is why the confirm dialog can honestly say the change cannot be
            // undone. Meanwhile the page-op modals have offered exactly this safety
            // net since they were written: "Save and continue" creates a version
            // first. The LESS destructive operation had a net and the MORE
            // destructive one did not.
            //
            // Deliberately FAIL-CLOSED: if the snapshot cannot be written we abort
            // rather than destroy the live project without a way back. That is a
            // behaviour change — a promote can now fail where it previously
            // proceeded — and it is the right trade.
            //
            // Placed here deliberately: AFTER every validation and read, so a
            // malformed URL or a failed fetch cannot leave a stray safety version
            // behind — and still BEFORE the PDF overwrite, since createVersion
            // copies the project's CURRENT pdf into the new version.
            //
            // `project` is always the live project, never the open version (see the
            // `project` getter), so this is correct even while viewing the version
            // being promoted.
            const promotedName = (version.name || version.created || 'a version').toString().slice(0, 80);
            const safetyName = `Before making "${promotedName}" live`;
            try {
                await dispatch('createVersion', {
                    name: safetyName,
                    autoSave: 'promote_backup',
                    autoSaveDetail: promoteDetail(promotedName)
                });
            } catch (snapshotError) {
                console.error('promoteVersion: aborting — could not snapshot the current version first:', snapshotError);
                throw new Error('Could not save a copy of your current project before making this version live. Nothing was changed.');
            }

            // Update PDF file
            await Vue.http.post(PROJECT_PROMOTE_VERSION_URL, {
                project_id: project.id,
                documentUrl: project.documentUrl,
                project_filePath: decodeURIComponent(projectMatches[1]),
                version_filePath: decodeURIComponent(versionMatches[1])
            });

            // Delete existing project data
            const projectBatch = new Batch();
            for (const cueDoc of projectCuesSnapshot.docs) {
                projectBatch.delete(cueDoc.ref);
            }
            for (const noteDoc of projectNotesSnapshot.docs) {
                projectBatch.delete(noteDoc.ref);
            }
            for (const layerDoc of projectLayersSnapshot.docs) {
                projectBatch.delete(layerDoc.ref);
            }

            // Structure markers are handled DIFFERENTLY from cues/notes/layers
            // here, deliberately (#386, decided 23 Aug 2026).
            //
            // Whether the version's structure can be restored is NOT the same
            // question as whether it has marker documents. An empty subcollection
            // means one of two opposite things:
            //
            //   `structureSnapshot: true`  — written by the fixed createVersion, so
            //       the project genuinely had no structure when it was saved. That
            //       empty set IS the snapshot. Restore it like any other
            //       subcollection: clear the live marks.
            //   flag absent — saved before #386, structure was never captured. Do
            //       NOT clear the live markers; mirroring the cue behaviour would
            //       silently destroy structure work during an action that does not
            //       present itself as destructive to structure.
            //
            // Counting documents conflated the two (fixed 12 Sept 2026). A version
            // saved after the fix but before the user drew any structure took the
            // pre-fix path, so promoting it KEPT marks placed against a different
            // pagination. With a page deleted in between they land a page out, and
            // the out-of-range sweep below cannot catch that: it only drops marks
            // past the restored page count, and restoring a LONGER document drops
            // nothing at all.
            const versionHasMarkers = versionMarkersSnapshot.docs.length > 0;
            const versionSnapshotsStructure = (versionDoc.data() || {}).structureSnapshot === true;
            const versionRestoresStructure = versionHasMarkers || versionSnapshotsStructure;
            const restoredPageCount = pdfPageNumbers.length ? pdfPageNumbers.length : null; // [] = no list, count unknown
            let markersDroppedOutOfRange = 0;

            for (const markerDoc of projectMarkersSnapshot.docs) {
                if (versionRestoresStructure) {
                    projectBatch.delete(markerDoc.ref);
                } else if (restoredPageCount !== null) {
                    // Pre-#386 only: keep the live marks, and drop just those left
                    // pointing past the end of the restored document, which is the
                    // genuinely broken case — promoting a 10-page version while a
                    // marker sits on page 18 leaves it on a page that is gone.
                    const pageIndex = Number((markerDoc.data() || {}).pageIndex);
                    if (Number.isFinite(pageIndex) && pageIndex > restoredPageCount) {
                        projectBatch.delete(markerDoc.ref);
                        markersDroppedOutOfRange++;
                    }
                }
            }
            if (markersDroppedOutOfRange > 0) {
                console.warn(
                    `promoteVersion: dropped ${markersDroppedOutOfRange} structure marker(s) ` +
                    `beyond the restored document's ${restoredPageCount} page(s). ` +
                    'The promoted version predates marker snapshotting (#386).'
                );
            }

            // Same cross-client signal every page-op sends (see renamePdfPages).
            // Promoting a version is a BIGGER change than any page-op -- it
            // overwrites the PDF file at the same URL and replaces every cue,
            // note and layer -- and it was the one path that never announced it.
            //
            // Without this, a collaborator with the project already open keeps
            // rendering the PDF they loaded while the cues underneath them are
            // replaced: old pages, new cues, no indication anything happened.
            // It is a stale in-memory render rather than a poisoned cache
            // (Firebase serves these with `cache-control: private, max-age=0`,
            // verified 10 Sept 2026, so any refresh revalidates and self-heals)
            // -- but nothing was prompting that refresh.
            //
            // The initiator skips, exactly as with a page-op, because their own
            // client reloads through the navigation at the end of this action.
            projectBatch.update(projectRef, {
                pdfPageNumbers: pdfPageNumbers,
                pdfReloadTick: Date.now(),
                pdfReloadBy: userUID || ''
            });
            await projectBatch.commit();

            // Copy version data to project
            const versionBatch = new Batch();
            const cuesRef = projectRef.collection('cues');
            const notesRef = projectRef.collection('notes');
            const layersRef = projectRef.collection('layers');

            for (const cueDoc of versionCuesSnapshot.docs) {
                versionBatch.set(cuesRef.doc(cueDoc.id), cueDoc.data());
            }
            for (const noteDoc of versionNotesSnapshot.docs) {
                versionBatch.set(notesRef.doc(noteDoc.id), noteDoc.data());
            }
            for (const layerDoc of versionLayersSnapshot.docs) {
                versionBatch.set(layersRef.doc(layerDoc.id), layerDoc.data());
            }
            // Ordering matters: deleteVersion below now removes the version's
            // scriptMarkers subcollection too, so the copy has to happen first or
            // the markers go with it.
            if (versionHasMarkers) {
                const markersRef = projectRef.collection('scriptMarkers');
                for (const markerDoc of versionMarkersSnapshot.docs) {
                    versionBatch.set(markersRef.doc(markerDoc.id), markerDoc.data());
                }
            }
            await versionBatch.commit();

            // KEEP the promoted version (23 Aug 2026). It used to be deleted here,
            // which came in with the original version system and was never revisited.
            // The only thing it bought was reclaiming that version's PDF; what it
            // cost was the ability to promote the same version again after further
            // edits, and a history log that quietly ate its own entries when used.
            //
            // deleteVersion also performed this unbind as a side effect. Without it
            // the store would stay bound to a version the user is navigating away
            // from, leaving `currentVersion` truthy — which suppresses the manage
            // dialog and would show the version's cues over the live project.
            if (currentVersion && currentVersion.id === versionID) {
                await dispatch('unsetActiveVersion', { ref: versionRef, callback: () => {} });
            }

            // After the promote has fully landed, so a prune can never cost the
            // backup it depends on. The promoted version itself is protected.
            await dispatch('pruneAutoSaves', { kind: 'promote_backup', protectIds: [versionID] });

            // Navigate
            if (currentVersion) {
                router.replace(`/project/${project.id}`);
            } else {
                NavBus.$emit('reloadCurrentProject', { newPagePosition: 1 });
            }
        } catch (error) {
            console.error('Error promoting version:', error);
            throw error;
        }
    },
    addVersionBookmark({ state, getters }, {versionID, pageNumber, pageTitle}) {
        const batch = new Batch();
        batch.update(db.collection('projects').doc(getters.project.id).collection('versions').doc(versionID), {
            ['bookmarks.' + pageNumber]: 'page ' + pageTitle
        });
        batch.commit();
    },
    removeVersionBookmark({ state, getters }, {versionID, pageNumber}) {
        const batch = new Batch();
        batch.update(db.collection('projects').doc(getters.project.id).collection('versions').doc(versionID), {
            ['bookmarks.' + pageNumber]: firebase.firestore.FieldValue.delete()
        });
        batch.commit();
    },
    // Renaming a bookmark in a saved version names the version's bookmark, the one its list shows (Josh, 25 Oct 2026).
    updateVersionBookmark({ state, getters }, {versionID, page, title}) {
        const batch = new Batch();
        batch.update(db.collection('projects').doc(getters.project.id).collection('versions').doc(versionID), {
            ['bookmarks.' + page]: title
        });
        return batch.commit();
    },
    addBookmark({ state, getters }, {pageNumber, pageTitle}) {
        return repos.users.update(getters.user.uid, {
            ['projects.' + getters.project.id + '.bookmarks.' + pageNumber]: 'page ' + pageTitle
        });
    },
    removeBookmark({ state, getters }, {pageNumber}) {
        return repos.users.update(getters.user.uid, {
            ['projects.' + getters.project.id + '.bookmarks.' + pageNumber]: firebase.firestore.FieldValue.delete()
        });
    },
    updateBookmark({ state, getters }, {page, title}) {
        return repos.users.update(getters.user.uid, {
            ['projects.' + getters.project.id + '.bookmarks.' + page]: title
        });
    },
    moveBookmark({ state, getters}, {from, to}) {
        return repos.users.update(getters.user.uid, {
            ['projects.' + getters.project.id + '.bookmarks.' + from]: firebase.firestore.FieldValue.delete(),
            ['projects.' + getters.project.id + '.bookmarks.' + to]: getters.bookmarks[from]
        });
    },
    // Apply a batch of bookmark page shifts (and removals) in ONE atomic Firestore
    // update. moveBookmark ran each move as its own update (delete `from`, set
    // `to`); when several bookmarks shift by the same offset their ranges overlap
    // (5->6, 6->7, ...) and, because those per-move updates raced, one move's
    // `delete 6` could wipe another move's `set 6` — losing bookmarks
    // nondeterministically after a page-op. Here every set wins over any delete of
    // the same key, so overlapping shifts (either direction) resolve correctly.
    // moves: [{ from, to }], removes: [pageNumber]. Returns the update promise.
    moveBookmarksBulk({ getters }, { moves = [], removes = [] }) {
        if (!getters.user || !getters.user.uid) return Promise.resolve();
        const base = 'projects.' + getters.project.id + '.bookmarks.';
        const current = getters.bookmarks || {};
        const payload = {};
        const setTargets = new Set();
        // Write destination keys first so they take precedence over any delete below.
        for (const { from, to } of moves) {
            const value = current[from];
            if (value !== undefined) {
                payload[base + to] = value;
                setTargets.add(String(to));
            }
        }
        // Clear vacated source keys + explicit removals, but never clear a key that
        // a move is writing into.
        for (const { from } of moves) {
            if (!setTargets.has(String(from))) {
                payload[base + from] = firebase.firestore.FieldValue.delete();
            }
        }
        for (const pageNumber of removes) {
            if (!setTargets.has(String(pageNumber))) {
                payload[base + pageNumber] = firebase.firestore.FieldValue.delete();
            }
        }
        if (Object.keys(payload).length === 0) return Promise.resolve();
        return repos.users.update(getters.user.uid, payload);
    },
    async mergeBookmark({ state, getters}, {from}) {
        try {
            // Rules plan S5b: only that member's settings for THIS project, not
            // their whole record (userByUID now returns a profile without it).
            const settings = await peerProjectSettings(from, getters.project.id);
            if (!settings) {
                console.error('User or projects not found');
                return;
            }

            const targetBookmarks = settings.bookmarks || {};
            const data = {};
            
            for (const page of Object.keys(targetBookmarks)) {
                data['projects.' + getters.project.id + '.bookmarks.' + page] = targetBookmarks[page];
            }
 
            await repos.users.update(getters.user.uid, data);
        } catch (error) {
            console.error('Error merging bookmarks:', error);
        }
    },
    updateUser({ state, getters }, {uid, update}) {
        return new Promise((resolve, reject) => {
            (async () => {
                try {
                    await repos.users.update(uid, update);
                    // Apply immediately so modals reopened before the Firestore
                    // snapshot arrives still show the new plan/cancel state.
                    if (state.activeUser && state.activeUser.uid === uid && update) {
                        // A FieldValue.delete() is removed locally, not copied in
                        // as a sentinel object (account rename clears first/last).
                        const del = firebase.firestore.FieldValue.delete();
                        const { assign, remove } = localUserPatch(update, v => v instanceof firebase.firestore.FieldValue && v.isEqual(del));
                        Object.assign(state.activeUser, assign);
                        remove.forEach(k => Vue.delete(state.activeUser, k));
                    }
                    resolve();
                } catch (e) {
                    console.error(e);
                    reject(e instanceof Error ? e : new Error(String(e)));
                }
            })();
        });
    },
    updateUserAffiliate({ state, getters }, { institution }) {
        repos.users.update(getters.user.uid, {
            affiliateInstitution: institution
        });
    },
    updateShowThumbnailPageUpdate({ state, getters }, showModeValue) {
        repos.users.update(getters.user.uid, {
            showThumbnailPageUpdate: showModeValue
        });
        state.showThumbnailPageUpdate = showModeValue;
    },
    updateShowMode({ state, getters }, showModeValue) {
        repos.users.update(getters.user.uid, {
            showMode: showModeValue
        });
        state.showMode = showModeValue;
    },
    updateMarginView({ state, getters }, {fieldKey, value}) {
        try {
            repos.users.update(getters.user.uid, {
                [fieldKey]: value
            });
            state[fieldKey] = value;
        } catch (e) {
            console.error(e);
        }
    },
    setNewProjectLoading({ state }, payload) {
        state.newProjectLoading = payload;
    },
    setDuplicateProjectLoading({ state }, payload) {
        state.newDuplicateProjectLoading = payload;
    },
    // Toggle the per-user pinned flag for a project. Writes the current user's
    // own doc (same path/permissions as toggleDashboardHide); the live
    // activeUser binding re-syncs user.projects so pinned/recent getters
    // recompute with no reload.
    togglePinProject({ getters }, { projectId, pinned }) {
        return repos.users.update(getters.user.uid, {
            ['projects.' + projectId + '.pinned']: pinned
        });
    },
    // Per-user, per-project: hide Act/Scene/Song marks on the script and in print.
    // Same path and permissions as togglePinProject above; the live activeUser
    // binding re-syncs user.projects so the getter recomputes with no reload, and
    // the change follows the user across tabs and devices.
    //
    // Returns the promise so the caller can revert and toast on failure. The
    // nearby layer setters swallow errors in a try/catch, which is exactly why a
    // failed write there is invisible — do not copy that shape.
    //
    // NOT guarded with set({}, {merge:true}) for a missing user doc: set() would
    // treat "projects.x.hideStructureMarks" as a literal field name containing
    // dots and create a broken key. A dotted-path update() is correct here, and
    // reaching this editor already implies the doc exists.
    setHideStructureMarks({ getters }, hidden) {
        const user = getters.user;
        const project = getters.project;
        if (!user || !user.uid || !project || !project.id) {
            return Promise.resolve();
        }
        return repos.users.update(user.uid, {
            ['projects.' + project.id + '.hideStructureMarks']: hidden === true
        });
    },
    // Duplicate a project. Extracted from the per-card menu so both grid and
    // list rows can call it. Clones the project object BEFORE deleting fields —
    // `project` is a reference into Vuex state, so mutating it would blank the
    // live card. Returns the new project id.
    async duplicateProject({ dispatch, getters }, { project }) {
        dispatch('setDuplicateProjectLoading', true);
        const user = getters.user;
        const projectCollectionRef = db.collection('projects');
        const newDuplicateProjectRef = projectCollectionRef.doc();
        const newProjectID = newDuplicateProjectRef.id;
        const now = new Date();

        // Clone before stripping transient/derived fields (do NOT mutate state).
        // Never inherit a Production Pass unlock, a Notes List show pass or a Notes
        // List link via duplication — a new project is a new production and must
        // consume its own pass, or stay locked. duplicateCopyFields strips every
        // server-owned field (firestore.rules refuses a create carrying one) and
        // writes the same reset values as the Project Settings duplicate.
        const src = duplicateCopyFields(project);
        delete src.totalCues;
        delete src.lastChangedDate;

        const myOldProjectPDF = src.documentUrl;
        const decodedURL = decodeURIComponent(myOldProjectPDF);
        const fileFullName = decodedURL.split('/').pop().split('?')[0];
        const fileName = fileFullName.substring(fileFullName.indexOf('-') + 1);

        const pdfBlob = await fetch(myOldProjectPDF).then(response => response.blob());
        const pdfBytes = await pdfBlob.arrayBuffer();
        const pdfDoc = await PDFJS.getDocument({ data: pdfBytes }).promise;
        const totalPages = pdfDoc.numPages;
        const updatedPdfPageNumbers = Array.from({ length: totalPages }, (_, i) => ({
            id: i,
            name: `${1 + i}`
        }));

        const pdfDirRef = firebase.storage().ref(`pdfs/${newProjectID}-${fileName}`);
        const downloadURL = await pdfDirRef.put(pdfBlob).then(snap => snap.ref.getDownloadURL());

        const newProject = {
            ...src,
            id: newProjectID,
            title: `${src.title} copy`,
            documentUrl: downloadURL,
            ownerUID: user.uid,
            lastChangedBy: user.uid,
            oldOwnerUID: src.ownerUID,
            oldProjectType: src.isNew || false,
            isDuplicate: true,
            lastChangedDate: now.toISOString(),
            collaborators: {},
            isNew: true,
            // A copy is a new project: History caps its automatic saves (history-autosave.js).
            autoSaveCap: true,
            pdfPageNumbers: updatedPdfPageNumbers
        };

        // A duplicated Show Pass show reverts to STANDARD project rules: the copy is
        // never unlocked and never consumes a pass (src.unlocked/passOwnerUID were
        // stripped above). Only fresh creation via create-project makes a Show Pass
        // project. So a non-subscriber who duplicates gets a read-only copy until they
        // subscribe — a duplicate is NOT a way to mint another Show Pass show.

        const batch = db.batch();
        // scriptMarkers added 23 Aug 2026 — #386's defect in a fourth place.
        // This is the ONLY duplicate: the dashboard menu and the Project Settings
        // button both dispatch it (12 Oct 2026). There used to be a second copy in
        // project-settings/nav.vue that drifted (it dropped markers, then copied a
        // Show Pass unlock); duplicate-project-fields.test.js fails if one reappears.
        //
        // Document ids are preserved by the copy below, so markers referencing
        // another marker's actId stay valid.
        const subCollections = ['cues', 'layers', 'notes', 'scriptMarkers'];
        let i = 0;
        const duplicationPromises = subCollections.map(async (subCollection) => {
            const snapshot = await db.collection('projects').doc(project.id).collection(subCollection).get();
            if (subCollection === 'cues' && i === 0) {
                newProject.totalCue = snapshot.size;
                newProject.totalCues = snapshot.size;
                i = 1;
            } else if (i === 0) {
                i = 1;
            }
            for (const doc of snapshot.docs) {
                const newDocRef = newDuplicateProjectRef.collection(subCollection).doc(doc.id);
                batch.set(newDocRef, doc.data());
            }
        });

        batch.set(newDuplicateProjectRef, newProject);
        await Promise.all(duplicationPromises);

        // The duplicator's own view of the show (Josh, 13 Oct 2026), not the owner's.
        const userProjectData = duplicatorProjectSettings(user, project.id, now);

        batch.update(db.collection('users').doc(user.uid), {
            [`projects.${newProjectID}`]: userProjectData
        });

        await batch.commit();
        return newProjectID;
    },
    renamePdfPages({ state, getters, dispatch }, {pdfPage}) {
        const batch = new Batch();
        // pdfReloadTick/By: a one-shot cross-client signal that the PDF page
        // structure changed (every page-op — replace/insert/delete/reorder from the
        // thumbnail view — funnels through here). Other clients watch this and reload
        // the PDF once; the initiator (pdfReloadBy === self) skips. Written in the same
        // batch as pdfPageNumbers so it lands exactly when the change is committed.
        batch.update(db.collection('projects').doc(getters.project.id), {
            pdfPageNumbers: pdfPage,
            pdfReloadTick: Date.now(),
            pdfReloadBy: (getters.user && getters.user.uid) || ''
        });
        return batch.commit().then(() => dispatch('queueNotesListStructureEvent', {
            event: 'structure.full'
        }));
    },
    // Load-time label repair (lib/page-labels.js healPageLabels). Was renamePdfPages, an
    // unconditional write: on 14 Oct a tab that reloaded mid page change wrote its stale
    // list over the labels the server had just finished. Now it writes only if the stored
    // labels are still the ones this tab saw.
    healPdfPageLabels({ getters, dispatch }, { expected, labels }) {
        return writeLabelsIfUnchanged({ db, projectId: getters.project && getters.project.id, expected, labels }).then((written) => {
            if (written) {
                return dispatch('queueNotesListStructureEvent', { event: 'structure.full' }).then(() => true);
            }
            return false;
        });
    },
    // Self-repair on open (7 Oct 2026, lib/page-labels.js extendedPageLabels). Writes
    // pdfPageNumbers ONLY, and only if the stored list still has the length the caller
    // saw: a second tab or collaborator that already extended it makes this a no-op.
    extendPdfPageLabels({ getters, dispatch }, { expectedLength, labels }) {
        const ref = db.collection('projects').doc(getters.project.id);
        return db.runTransaction(async (tx) => {
            const snap = await tx.get(ref);
            const current = snap.exists ? snap.data().pdfPageNumbers : null;
            if (!Array.isArray(current) || current.length !== expectedLength || labels.length <= current.length) {
                return false;
            }
            tx.update(ref, { pdfPageNumbers: labels });
            return true;
        }).then((written) => {
            if (written) {
                return dispatch('queueNotesListStructureEvent', { event: 'structure.full' }).then(() => true);
            }
            return false;
        });
    },
    renameVersionPdfPages({ state, getters }, {pdfPage}) {
        const batch = new Batch();
        batch.update(db.collection('projects').doc(getters.project.id).collection('versions').doc(getters.currentVersion.id), {
            pdfPageNumbers: pdfPage
        });
        return batch.commit();
    },
    // Palette Tool
    setActiveTool({ state, getters }, {type}) {
        state.activeTool = type || 'pointerSelect';
        localStorage.setItem('note-tool-selected', state.activeTool);
    },
    setDrwaOrLine({ state, getters }, {type}) {
        state.activeDrawTool = type || 'free_drawing';
        localStorage.setItem('note-draw-selected', state.activeDrawTool);
    },
    setSelectedPageType({ state, getters }, {type}) {
        state.pageType = type || 'no';
        // Keyed per project: opening project B must not inherit A's layout.
        // See lib/view-mode-storage.js -- and note mounted() has to re-seed it.
        writeViewSetting('pageType', getters.project && getters.project.id, state.pageType);
    },
    setScale({ state, getters }, {type}) {
        state.pageScale = type || 1;
        // Keyed per project: zoom is per-script, not per-account. See
        // lib/page-scale-storage.js for why this is localStorage and not the
        // user doc.
        writePageScale(getters.project && getters.project.id, state.pageScale);
    },
    setThumbnailView({ state, getters }, {value}) {
        state.thumbnailView = value;
        writeViewSetting('thumbnailView', getters.project && getters.project.id, state.thumbnailView);
    },
    setSplitView({ state, getters }, {value}) {
        state.splitView = value;
        writeViewSetting('splitView', getters.project && getters.project.id, state.splitView);
    },
    setSplitPageSync({ state, getters }, {value}) {
        state.splitPageSync = value;
        writeViewSetting('splitPageSync', getters.project && getters.project.id, state.splitPageSync);
    },
    setSplitRatio({ state, getters }, {value}) {
        state.splitRatio = value;
        writeViewSetting('splitRatio', getters.project && getters.project.id, state.splitRatio);
    },
    setLoader({ state, getters }, {type}) {
        state.pageLoader = type || false;
        localStorage.setItem('page-loader', state.pageLoader);
    },
    setLoaderLogin({ state, getters }, {type}) {
        state.pageLoaderLogin = type || false;
        localStorage.setItem('page-loader-login', state.pageLoaderLogin);
    },
    async updatelockDuplicateSettingProject({ getters }, {duplicateSetting}) {
        db.collection('projects').doc(getters.project.id).update({
            duplicateSetting
        });
    },
    async projectBannerDisplay({ getters }, {projectBannerDisplay}) {
        db.collection('projects').doc(getters.project.id).update({
            projectBannerDisplay
        });
    },
    async projectBannerUser({ getters }, {userid}) {
        db.collection('projects').doc(getters.project.id).update({
            projectBannerUser: userid
        });
    },
    // The page-change lock, taken at the click and awaited (lib/page-change-lock.js, Josh 14 Oct).
    // Then this tab's unsent edits to the show must reach the server before anything changes
    // (lib/show-edits-sent.js, bounded); if they cannot, the lock is lifted and nothing starts.
    async takePageChangeLock({ getters }) {
        const projectId = getters.project.id;
        const uid = getters.user.uid;
        const result = await takePageChangeLock({ db, projectId, uid });
        if (result !== 'taken') return result;
        try {
            await waitForShowEditsSent({ db, projectId });
        } catch (error) {
            await releasePageChangeLock({ db, projectId, uid }).catch(() => {});
            throw error;
        }
        return 'taken';
    },
    // Lifts the lock only if this person holds it. Never throws: a failed release is left to the
    // existing clean-ups (reopening the thumbnails, the owner's Close anyway).
    async releasePageChangeLock({ getters }) {
        try {
            return await releasePageChangeLock({ db, projectId: getters.project.id, uid: getters.user.uid });
        } catch (e) {
            console.error('[page-change] could not release the lock:', e);
            return 'error';
        }
    },
    ...integrationActions,
    ...markerActions
};


// WEBPACK FOOTER //
// ./src/lib/vuex/actions.js